import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD } from "@/lib/dates";
import { STANDARD_PRICING, PRO_PLANS } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const STANDARD_ROWS = [
  { key: "A", label: null, duration: "3 Months" },
  { key: "B", label: null, duration: "6 Months" },
  { key: "C", label: null, duration: "12 Months" },
];

/**
 * ChangePlanModal — supports 4 axes:
 *  - Standard: path (A|B|C) + debtor_count (1|2|3)
 *  - PRO: cadence (monthly|annual)
 * Delegates to PATCH /subscription.
 */
const ChangePlanModal = ({ open, subscription, onClose, onChanged, initialPlanType, initialProCadence }) => {
  const [planType, setPlanType] = useState(subscription?.plan_type || "standard");
  const [path, setPath] = useState(subscription?.path || "A");
  const [debtorCount, setDebtorCount] = useState(subscription?.debtor_count || 1);
  const [proCadence, setProCadence] = useState(
    subscription?.plan_type === "pro" ? subscription?.path || "monthly" : "monthly"
  );
  const [submitting, setSubmitting] = useState(false);

  // Reset local state whenever the modal opens with a fresh subscription.
  // Allow callers to pre-select a plan (e.g. "upgrade to PRO" deep-link from
  // the sidebar/debtors slot-meter → arrives with initialPlanType="pro").
  React.useEffect(() => {
    if (!open || !subscription) return;
    const pt = initialPlanType || subscription.plan_type || "standard";
    setPlanType(pt);
    setPath(pt === "standard" ? (subscription.path || "A") : "A");
    setDebtorCount(subscription.debtor_count || 1);
    setProCadence(
      pt === "pro"
        ? (initialProCadence || (subscription.plan_type === "pro" ? subscription.path : "monthly") || "monthly")
        : "monthly"
    );
  }, [open, subscription, initialPlanType, initialProCadence]);

  const newPriceCents = useMemo(() => {
    if (planType === "pro") {
      const p = PRO_PLANS.find((pl) => pl.key === proCadence);
      return (p?.price || 0) * 100;
    }
    return (STANDARD_PRICING?.[debtorCount]?.[path] || 0) * 100;
  }, [planType, path, debtorCount, proCadence]);

  const currentPrice = subscription?.price_cents || 0;
  const delta = newPriceCents - currentPrice;

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = planType === "pro"
        ? { plan_type: "pro", path: proCadence }
        : { plan_type: "standard", path, debtor_count: debtorCount };
      const { data } = await apiClient.patch("/subscription", body);
      toast.success("Plan updated.");
      onChanged?.(data.subscription);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={submitting ? undefined : onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="change-plan-modal"
          >
            <div
              className="w-full max-w-[680px] max-h-[92vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-8 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="jea-caption">Subscription</div>
                  <h2 className="text-white font-bold text-[22px]">Change Plan</h2>
                  <p className="text-text-secondary text-[13px] mt-1">
                    Prorated charges are simulated. No real card is charged.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="p-2 text-text-secondary hover:text-white disabled:opacity-40"
                  data-testid="change-plan-close"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Plan-type toggle */}
              <div className="jea-caption mb-2">Plan Type</div>
              <div className="grid grid-cols-2 gap-2 mb-5" data-testid="change-plan-type-toggle">
                {[
                  { key: "standard", label: "Standard" },
                  { key: "pro", label: "PRO" },
                ].map((pt) => {
                  const active = planType === pt.key;
                  return (
                    <button
                      key={pt.key}
                      type="button"
                      onClick={() => setPlanType(pt.key)}
                      className={cn(
                        "h-11 rounded-[8px] border text-[14px] font-semibold transition-all",
                        active
                          ? "bg-accent-pink text-[#091424] border-accent-pink"
                          : "bg-bg-primary text-text-secondary border-border-default hover:text-white",
                      )}
                      data-testid={`change-plan-type-${pt.key}`}
                    >
                      {pt.label}
                    </button>
                  );
                })}
              </div>

              {planType === "standard" ? (
                <>
                  {/* Judgment count */}
                  <div className="jea-caption mb-2">Judgment Count</div>
                  <div className="grid grid-cols-3 gap-2 mb-5" data-testid="change-plan-debtor-count">
                    {[1, 2, 3].map((n) => {
                      const active = debtorCount === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setDebtorCount(n)}
                          className={cn(
                            "h-11 rounded-[8px] border text-[14px] font-semibold transition-all",
                            active
                              ? "bg-accent-pink text-[#091424] border-accent-pink"
                              : "bg-bg-primary text-text-secondary border-border-default hover:text-white",
                          )}
                          data-testid={`change-plan-debtor-count-${n}`}
                        >
                          {n} {n === 1 ? "Judgment" : "Judgments"}
                        </button>
                      );
                    })}
                  </div>

                  {/* Path */}
                  <div className="jea-caption mb-2">Path</div>
                  <div className="grid grid-cols-3 gap-2 mb-5" data-testid="change-plan-path">
                    {STANDARD_ROWS.map((r) => {
                      const active = path === r.key;
                      const price = STANDARD_PRICING[debtorCount]?.[r.key] || 0;
                      return (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => setPath(r.key)}
                          className={cn(
                            "flex flex-col items-center justify-center h-16 rounded-[8px] border transition-all",
                            active
                              ? "bg-accent-pink/10 border-accent-pink text-accent-pink"
                              : "bg-bg-primary border-border-default text-text-secondary hover:text-white",
                          )}
                          data-testid={`change-plan-path-${r.key}`}
                        >
                          <div className="text-[11px] tracking-[0.06em] uppercase font-semibold">
                            {r.duration}
                          </div>
                          <div className="jea-mono text-[15px] font-bold mt-0.5">${price}</div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className="jea-caption mb-2">Cadence</div>
                  <div className="grid grid-cols-2 gap-3 mb-5" data-testid="change-plan-cadence">
                    {PRO_PLANS.map((p) => {
                      const active = proCadence === p.key;
                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setProCadence(p.key)}
                          className={cn(
                            "text-left p-4 rounded-[10px] border transition-all",
                            active
                              ? "bg-accent-pink/10 border-accent-pink shadow-pink-glow"
                              : "bg-bg-primary border-border-default hover:border-accent-pink/40",
                          )}
                          data-testid={`change-plan-cadence-${p.key}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-white font-semibold text-[14px]">{p.label}</div>
                            {p.badge && (
                              <span className="text-[10px] font-bold tracking-[0.06em] uppercase px-2 py-[2px] rounded-full bg-accent-pink/15 text-accent-pink border border-accent-pink/40">
                                {p.badge}
                              </span>
                            )}
                          </div>
                          <div className="jea-mono text-accent-pink font-bold text-[18px] mt-1">{p.duration}</div>
                          <div className="text-text-secondary text-[12px] mt-1">{p.subtext}</div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Summary */}
              <div className="rounded-[12px] bg-bg-primary border border-border-default p-4" data-testid="change-plan-summary">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-text-secondary">Current price</span>
                  <span className="jea-mono text-text-secondary">{centsToUSD(currentPrice)}</span>
                </div>
                <div className="flex items-center justify-between text-[13px] mt-1">
                  <span className="text-text-secondary">New price</span>
                  <span className="jea-mono text-white font-semibold">{centsToUSD(newPriceCents)}</span>
                </div>
                <div className="flex items-center justify-between text-[14px] mt-3 pt-3 border-t border-border-default">
                  <span className="text-text-secondary">Prorated today (sim.)</span>
                  <span className={cn(
                    "jea-mono font-bold",
                    delta > 0 ? "text-accent-pink" : delta < 0 ? "text-success" : "text-text-muted"
                  )}>
                    {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${centsToUSD(delta)}`}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                  data-testid="change-plan-cancel"
                >
                  Cancel
                </button>
                <JeaButton onClick={submit} loading={submitting} className="gap-2" data-testid="change-plan-submit">
                  {submitting ? (<><Loader2 size={14} className="animate-spin" /> Updating…</>) : (<><Check size={14} /> Confirm Change</>)}
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChangePlanModal;
